import React, { useEffect, useRef, useState } from 'react';
import '.././styles/nmtai.scss';
import { alert_chat, scan_alert_risk_data, get_fields_measures_metadata, upsert_fields_measures_metadata } from '../services';
import ButtonLoader from '../Components/ButtonLoader';
import ReactMarkdown from 'react-markdown';
import TooltipText from '../Components/TooltipText';

function NmtAI({ initializedData, remainingCredits: parentRemainingCredits, onCreditsUpdate }) {
    const [query, setQuery] = useState('');
    const [suggestionsVisible, setSuggestionsVisible] = useState(false);
    const suggestionBoxRef = useRef(null);
    const [selectedPrompt, setSelectedPrompt] = useState('');
    const [aiResponses, setAiResponses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [resMesg, setResMesg] = useState('');
    const [showAIResponses, setShowAIResponses] = useState(false);
    const userName = localStorage.getItem('email')
    const firstLetter = userName ? userName.charAt(0).toUpperCase() : '';
    const [displayName, setDisplayName] = useState('');
    const [copied, setCopied] = useState(false);
    const [remainingCredits, setRemainingCredits] = useState(() => (initializedData?.remaining_credits ?? parentRemainingCredits ?? 0));
    const [alertRiskData, setAlertRiskData] = useState([]);
    const [alertRiskLoading, setAlertRiskLoading] = useState(false);

    // Column management states
    const [fieldsMetadata, setFieldsMetadata] = useState([]);
    const DEFAULT_VISIBLE_COLUMNS = ['Risk Band', 'Risk Statement', 'Attack Chain', 'Framework Mapping', 'Industry Benchmark', 'Recommended Action'];
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [columnWidths, setColumnWidths] = useState({});
    const [showColumnSettings, setShowColumnSettings] = useState(false);
    const columnSettingsRef = useRef(null);
    const [modalRow, setModalRow] = useState(null);
    const chatBottomRef = useRef(null);

    // Format header: remove leading _, convert to Title Case
    const formatHeader = (key) => {
        return key
            .replace(/^_+/, '')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
    };

    // Universal string formatter — handles any data pattern:
    //
    // PASS THROUGH unchanged (preserve meaning/formatting):
    //   - URLs (http/https/ftp)
    //   - Prose sentences (contain spaces + punctuation, no underscores)
    //   - Numbers, percentages, financial values ($, £, €)
    //   - Dates, times, version strings (e.g. "2024-01-01", "v1.2.3")
    //   - Code-like strings with mixed symbols (e.g. "CC6.1 (Logical Access)")
    //   - ALL-CAPS acronyms (e.g. "NIST", "SOC2", "ALE")
    //
    // TRANSFORM (normalize to human-readable):
    //   - snake_case            → Title Case  (e.g. "web_app_chain" → "Web App Chain")
    //   - kebab-case            → Title Case  (e.g. "my-key-name" → "My Key Name")
    //   - camelCase             → Title Case  (e.g. "riskSeverity" → "Risk Severity")
    //   - PascalCase            → Title Case  (e.g. "RiskSeverity" → "Risk Severity")
    //   - mixed snake+spaces    → Title Case  (e.g. "isolated_High score" → "Isolated High Score")
    //   - SCREAMING_SNAKE_CASE  → Title Case  (e.g. "MAX_VALUE" → "Max Value")
    const formatStringValue = (str) => {
        if (typeof str !== 'string') return String(str);
        if (!str.trim()) return str;

        // 1. Preserve URLs
        if (/^(https?|ftp):\/\//i.test(str)) return str;

        // 2. Preserve version strings (e.g. "v1.2.3", "2.0.0-beta")
        if (/^v?\d+\.\d+/.test(str)) return str;

        // 3. Preserve date strings (ISO, slash, dash formats e.g. "2024-01-01", "01/01/2024")
        if (/^\d{4}-\d{2}-\d{2}/.test(str) || /^\d{2}[\/\-]\d{2}[\/\-]\d{4}/.test(str)) return str;

        // 4. Preserve pure numbers / percentages / financial values
        if (/^[\$£€]?[\d,]+\.?\d*(%|\/yr|\/mo|\/yr)?$/.test(str.trim())) return str;

        // 5. Prose: has spaces AND no underscores AND not pure camelCase/PascalCase identifier
        //    Treat as prose (free text) — pass through as-is
        const hasSpaces = str.includes(' ');
        const hasUnderscores = str.includes('_');
        const hasHyphens = /^[a-z0-9-]+$/.test(str); // pure kebab-case (no spaces)
        const isCamelOrPascal = /^[A-Za-z][a-zA-Z0-9]*$/.test(str) && /[a-z][A-Z]|^[A-Z][a-z]/.test(str);

        if (hasSpaces && !hasUnderscores && !isCamelOrPascal) return str;

        // 6. snake_case / SCREAMING_SNAKE / mixed snake+spaces → replace _ with space → Title Case
        if (hasUnderscores) {
            return str
                .replace(/_+/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
                .replace(/\b\w/g, c => c.toUpperCase());
        }

        // 7. Pure kebab-case → replace - with space → Title Case
        if (hasHyphens && str.includes('-')) {
            return str
                .replace(/-+/g, ' ')
                .trim()
                .replace(/\b\w/g, c => c.toUpperCase());
        }

        // 8. camelCase / PascalCase → insert space before uppercase letters
        if (isCamelOrPascal) {
            return str
                .replace(/([a-z])([A-Z])/g, '$1 $2')       // camelCase → camel Case
                .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // acronym boundary: XMLParser → XML Parser
                .replace(/\b\w/g, c => c.toUpperCase());
        }

        return str;
    };

    // Format cell value to plain readable text
    const cellValue = (value) => {
        if (value === null || value === undefined || value === '') return '—';
        if (Array.isArray(value)) {
            if (value.length === 0) return '—';
            const hasObjects = value.some(item => typeof item === 'object' && item !== null);
            return value.map(item => {
                if (typeof item === 'object' && item !== null) {
                    return Object.entries(item)
                        .filter(([, v]) => v !== null && v !== undefined && v !== '')
                        .map(([k, v]) => `${formatHeader(k)}: ${formatStringValue(String(v))}`)
                        .join(' | ');
                }
                return formatStringValue(String(item));
            }).join(hasObjects ? ' • ' : ', ');
        }
        if (typeof value === 'object') {
            return Object.entries(value)
                .filter(([, v]) => v !== null && v !== undefined && v !== '')
                .map(([k, v]) => `${formatHeader(k)}: ${formatStringValue(String(v))}`)
                .join(' | ');
        }
        return formatStringValue(value);
    };

    // For columns like Risk Band: show only the label before ' - ' in the table cell
    const cellSummary = (key, value) => {
        if (key === 'Risk Band') {
            const full = cellValue(value);
            if (typeof full === 'string' && full.includes(' - ')) {
                return full.split(' - ')[0].trim();
            }
        }
        return cellValue(value);
    };

    const isLongText = (value) => {
        const v = cellValue(value);
        return typeof v === 'string' && v.length > 80;
    };

    // Minimum column width = width of the longest single word in the header label
    // (uppercase 11px 700 weight ≈ 7.8 px/char + 38 px horizontal padding)
    const getMinColumnWidth = (header) => {
        const words = header.split(/\s+/).filter(Boolean);
        const longestWord = words.reduce((a, b) => (a.length >= b.length ? a : b), '');
        return Math.max(80, Math.round(longestWord.length * 7.8) + 38);
    };
    useEffect(() => {
        const email = localStorage.getItem('email');
        if (email) {
            const name = formatNameFromEmail(email);
            setDisplayName(name);
        }
    }, []);

    useEffect(() => {
        fetchAlertRiskData();
        fetchFieldsMetadata();
    }, []);

    const fetchAlertRiskData = async () => {
        setAlertRiskLoading(true);
        try {
            const organization_id = localStorage.getItem('organization_id');
            const response = await scan_alert_risk_data({ organization_id });
            const records = response?.statusCode?.response_data?.data?.records || [];
            // Records come back newest-first. The newest scan may have 0 findings and
            // an empty ai_response (which is truthy as []), so pick the most recent
            // record that actually has AI risk data instead of blindly using records[0].
            const recordWithData = records.find(
                r => Array.isArray(r?.ai_response) && r.ai_response.length > 0
            );
            if (recordWithData) {
                const data = recordWithData.ai_response;
                setAlertRiskData(data);
                // Auto-hide all columns except the 5 default visible ones
                if (data.length > 0) {
                    const allKeys = Object.keys(data[0]);
                    setHiddenColumns(allKeys.filter(k => !DEFAULT_VISIBLE_COLUMNS.includes(k)));
                }
            }
        } catch (error) {
            console.error('Error fetching alert risk data:', error);
        } finally {
            setAlertRiskLoading(false);
        }
    };

    const fetchFieldsMetadata = async () => {
        try {
            const response = await get_fields_measures_metadata();
            const data = response?.statusCode?.response_data?.data || [];

            if (data.length > 0) {
                // API has data — use hide flag from API
                setFieldsMetadata(data);
                const hidden = data.filter(item => item.hide).map(item => item.field);
                setHiddenColumns(hidden);
                // Restore saved column widths
                const widths = {};
                data.forEach(item => {
                    if (item.required_measure && !isNaN(item.required_measure)) {
                        widths[item.field] = Number(item.required_measure);
                    }
                });
                if (Object.keys(widths).length > 0) {
                    setColumnWidths(widths);
                }
            } else {
                // API returned no data — show all columns by default
                setFieldsMetadata([]);
                setHiddenColumns([]);
            }
        } catch (error) {
            console.error('Error fetching fields metadata:', error);
            setHiddenColumns([]);
        }
    };



    const handleColumnToggle = async (header) => {
        const isCurrentlyHidden = hiddenColumns.includes(header);
        const newHidden = isCurrentlyHidden
            ? hiddenColumns.filter(h => h !== header)
            : [...hiddenColumns, header];
        setHiddenColumns(newHidden);

        try {
            await upsert_fields_measures_metadata({
                category: 'risk_quantification',
                field: header,
                required_measure: header,
                hide: !isCurrentlyHidden
            });
        } catch (error) {
            console.error('Error updating column visibility:', error);
            // Revert on failure
            setHiddenColumns(hiddenColumns);
        }
    };

    // Column resize handlers - using direct DOM manipulation for smooth performance
    const handleMouseDown = (e, header) => {
        e.preventDefault();
        e.stopPropagation();
        const th = e.target.closest('th');
        if (!th) return;

        const startXPos = e.clientX;
        const startWidthVal = th.offsetWidth;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const minWidth = getMinColumnWidth(header);

        const onMouseMove = (moveEvent) => {
            const diff = moveEvent.clientX - startXPos;
            const newWidth = Math.max(minWidth, startWidthVal + diff);
            th.style.width = `${newWidth}px`;
            th.style.minWidth = `${newWidth}px`;
        };

        const onMouseUp = () => {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);

            // Get final width and update state + API
            const finalWidth = th.offsetWidth;
            setColumnWidths(prev => ({ ...prev, [header]: finalWidth }));
            upsert_fields_measures_metadata({
                category: 'risk_quantification',
                field: header,
                required_measure: String(finalWidth),
                hide: hiddenColumns.includes(header)
            }).catch(err => console.error('Error saving column width:', err));
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    // Close column settings dropdown on outside click
    useEffect(() => {
        const handleClickOutsideSettings = (e) => {
            if (columnSettingsRef.current && !columnSettingsRef.current.contains(e.target)) {
                setShowColumnSettings(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideSettings);
        return () => document.removeEventListener('mousedown', handleClickOutsideSettings);
    }, []);

    const formatNameFromEmail = (email) => {
        const namePart = email.split('@')[0];
        const parts = namePart.split('.');
        return parts.map(part =>
            part.charAt(0).toUpperCase() + part.slice(1)
        ).join(' ');
    };
    const dynamicHeaders = alertRiskData.length > 0
        ? (() => {
            const allKeys = Object.keys(alertRiskData[0]).filter(h => {
                // Remove columns where every row has empty/null/empty-array value
                return alertRiskData.some(row => {
                    const v = row[h];
                    if (v === null || v === undefined || v === '') return false;
                    if (Array.isArray(v) && v.length === 0) return false;
                    return true;
                });
            });
            // Order: Risk Band first (if present), then Risk Statement, then rest
            const priority = ['Risk Band', 'Risk Statement'];
            const rest = allKeys.filter(k => !priority.includes(k));
            const ordered = [...priority.filter(k => allKeys.includes(k)), ...rest];
            return ordered;
        })()
        : [];
    const visibleHeaders = dynamicHeaders.filter(h => !hiddenColumns.includes(h));
    const headerLabel = (key) => formatHeader(key);

    const handleDownload = () => {
        if (dynamicHeaders.length === 0 || alertRiskData.length === 0) return;

        const csvContent = [
            dynamicHeaders.map(h => `"${headerLabel(h)}"`).join(','),
            ...alertRiskData.map(row =>
                dynamicHeaders.map(key => `"${String(cellValue(row[key])).replace(/"/g, '""')}"`).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'cyber_risks_ai_report.csv';
        link.click();
    };


    const promptSuggestions = [
        // "Which of these top risks should I fix first based on exploitability and business impact?",
        // "Generate a step-by-step remediation plan for my top 5 risks.",
        // "Explain how these risks affect my compliance readiness (ISO 27001 / SOC 2 / DPDP).",
        // "Show me what has changed since my last scan — what’s new, resolved, or still critical.",
        // "Prepare a summary of my current risks and fixes for my leadership team."
        "What decision should I make this week?",
        "What is my highest financial risk today?",
        "What should the board know right now?",
    ];


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionBoxRef.current && !suggestionBoxRef.current.contains(event.target)) {
                setSuggestionsVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const sendAiQuery = async () => {
        if (!selectedPrompt && !query.trim()) return;

        setIsLoading(true);
        const organization_id = localStorage.getItem('organization_id');
        const params = {
            organization_id: Number(organization_id),
            question: selectedPrompt || query
        };

        try {
            const response = await alert_chat(params);
            const responseData = response?.statusCode?.response_data?.data?.response || '';
            const questionText = selectedPrompt || query;

            setResMesg(response?.statusCode?.error?.message || '');
            if (response?.statusCode?.response_data?.status_code === 200) {
                setAiResponses(prev => [...prev, { question: questionText, answer: responseData }]);

                // Update remaining credits here
                const updatedCredits = response?.statusCode?.response_data?.data?.remaining_credits;
                if (updatedCredits !== undefined) {
                    setRemainingCredits(updatedCredits);
                    if (typeof onCreditsUpdate === 'function') onCreditsUpdate(updatedCredits);
                }
            } else {
                setAiResponses(prev => [...prev, { question: questionText, answer: 'Error fetching AI response. Please try again.' }]);
            }

        } catch {
            const questionText = selectedPrompt || query;
            setAiResponses(prev => [...prev, { question: questionText, answer: 'Error fetching AI response. Please try again.' }]);
        } finally {
            setShowAIResponses(true);
            setSuggestionsVisible(false);
            setQuery('');
            setSelectedPrompt('');
            setIsLoading(false);
        }
    };



    const handleCopy = (item) => {
        const textToCopy = item?.answer?.replace(/```markdown\n([\s\S]*?)```/, "$1") || "";
        navigator.clipboard.writeText(textToCopy).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500); // Reset after 1.5s
        });
    };
    useEffect(() => {
        if (parentRemainingCredits !== undefined && parentRemainingCredits !== null) {
            setRemainingCredits(parentRemainingCredits);
        } else {
            setRemainingCredits(initializedData?.remaining_credits ?? 0);
        }
    }, [parentRemainingCredits, initializedData]);

    useEffect(() => {
        if (showAIResponses && chatBottomRef.current) {
            chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [aiResponses, showAIResponses]);

    return (
        <div>
            <div className="cm_controls">
                <button className="download-btn" onClick={handleDownload}>Download</button>

                <div className="position-relative" ref={columnSettingsRef}>
                    <button
                        className="download-btn"
                        onClick={() => setShowColumnSettings(!showColumnSettings)}
                        style={{ background: '#0F172A', color: '#fff', border: '1px solid rgba(226, 232, 240, 0.3)', padding: '7px 12px', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}
                    >
                        ⚙ Columns
                    </button>

                    {showColumnSettings && (
                        <div className="column-settings-dropdown" style={{
                            position: 'absolute', top: '37px', right: 0, zIndex: 20,
                            background: '#1e293b', border: '1px solid #334155', borderRadius: '8px',
                            padding: '10px', minWidth: '200px', height: '200px', overflowY: 'auto'
                        }}>
                            <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '8px', fontWeight: '600' }}>Show/Hide Columns</p>
                            {dynamicHeaders.map((header, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 4px', cursor: 'pointer' }}
                                    onClick={() => handleColumnToggle(header)}>
                                    <input
                                        type="checkbox"
                                        checked={!hiddenColumns.includes(header)}
                                        onChange={() => { }}
                                        style={{ accentColor: '#3b82f6', cursor: 'pointer' }}
                                    />
                                    <span style={{ color: '#fff', fontSize: '13px' }}>{headerLabel(header)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className={`nmt_ai_container show_scrollbar${!alertRiskLoading ? ' nmt_ai_container--ready' : ''}`}>

                <div className='nmt_ai_section'>
                    <div className="nmt_ai_scrollable">
                        <div className="nmt_ai_header">
                            <div></div>
                        </div>

                        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                            <div className={`table-scroll ${alertRiskData?.length > 5 ? 'scrollable' : ''}`}>
                                <table style={{ tableLayout: 'fixed', width: fieldsMetadata.length > 0 ? `${visibleHeaders.reduce((sum, h) => sum + (columnWidths[h] || 200), 0)}px` : '100%', minWidth: '100%' }}>
                                    <thead>
                                        <tr>
                                            {visibleHeaders.map((header, idx) => (
                                                <th key={idx} style={{
                                                    width: columnWidths[header] ? `${columnWidths[header]}px` : 'auto',
                                                    minWidth: `${getMinColumnWidth(headerLabel(header))}px`,
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 5,
                                                    userSelect: 'none',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'normal',
                                                    overflowWrap: 'normal',
                                                    verticalAlign: 'top',
                                                }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                        {headerLabel(header)}
                                                        {header === 'Dwell Time' && (
                                                            <TooltipText
                                                                src='https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/hover-text.svg'
                                                                alt="info"
                                                                tooltipText="Dwell Time is how long this risk has been present in your environment. A longer dwell time increases urgency — the multiplier shown reflects compounded exposure over time."
                                                                className='info_text'
                                                                height={14}
                                                                width={14}
                                                            />
                                                        )}
                                                    </span>
                                                    <span
                                                        className="col-resize-handle"
                                                        onMouseDown={(e) => handleMouseDown(e, header)}
                                                        style={{
                                                            position: 'absolute', right: 0, top: 0, bottom: 0,
                                                            width: '4px', cursor: 'col-resize',
                                                        }}
                                                    />
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {alertRiskLoading ? (
                                            <tr>
                                                <td colSpan={visibleHeaders.length || 1} style={{ color: '#fff', textAlign: 'center' }}>
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : alertRiskData.length > 0 ? (
                                            alertRiskData.map((row, index) => (
                                                <tr
                                                    key={index}
                                                    onClick={() => setModalRow(row)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {visibleHeaders.map((key, idx) => (
                                                        <td
                                                            key={idx}
                                                            title={String(cellValue(row[key]))}
                                                            style={{ width: columnWidths[key] ? `${columnWidths[key]}px` : 'auto' }}
                                                        >
                                                            {isLongText(row[key]) ? (
                                                                <span className="cell-truncate">{cellSummary(key, row[key])}</span>
                                                            ) : (
                                                                cellSummary(key, row[key])
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={visibleHeaders.length || 1} style={{ textAlign: 'center' }}>
                                                    No data available.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {showAIResponses && (

                            <div className="ai_responses_list">
                                {aiResponses?.map((item, idx) => (
                                    <div key={idx} className="chat_wrapper">
                                        <div className='client_name d-flex align-items-center gap-3'>
                                            {displayName}
                                            <div className='first_letter'>
                                                {firstLetter ? firstLetter : ''}
                                            </div>
                                        </div>
                                        <div className="chat_bubble user">
                                            <div className="bubble_content">{item?.question}</div>
                                        </div>
                                        <div>
                                            <div className='ai_response'>
                                                <img src='https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/profile-user.svg' alt='logo' height={38} width={38} />
                                                <span className='ai_text font_14 font_600 color_white'>NOVA AI</span>
                                            </div>
                                            <div className="chat_bubble ai">
                                                <div className="bubble_content_ai">
                                                    <ReactMarkdown>
                                                        {item?.answer.replace(/```markdown\n([\s\S]*?)```/, '$1')}
                                                    </ReactMarkdown>
                                                </div>
                                                <span className='chat_middle_line'></span>
                                                <div>
                                                    <button className="copy-btn" onClick={() => handleCopy(item)}>
                                                        {copied ? "Copied!" : "Copy"}{" "}
                                                        <img src="https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/copy-icon.svg" alt="copy" height={18} width={18} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div ref={chatBottomRef} />
                            </div>
                        )}
                    </div>

                    <div className="nmt_ai_chat_bar" ref={suggestionBoxRef}>
                        <input
                            type="text"
                            placeholder={isLoading ? "Getting response..." : "Ask me anything..."}
                            maxLength={270}
                            value={selectedPrompt || query}
                            disabled={isLoading}
                            onFocus={() => {
                                setSuggestionsVisible(true);
                            }}
                            onBlur={() => {
                                setTimeout(() => setSuggestionsVisible(false), 150);
                            }}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (selectedPrompt) setSelectedPrompt('');
                                setQuery(val);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isLoading && (selectedPrompt || query.trim())) {
                                    sendAiQuery();
                                }
                            }}
                        />

                        {suggestionsVisible && !isLoading && promptSuggestions.length > 0 && (
                            <div className="suggestions-box">
                                {promptSuggestions.map((prompt, index) => (
                                    <div
                                        key={index}
                                        className="suggestion-item"
                                        onMouseDown={(e) => {
                                            e.preventDefault(); // prevent blur before click
                                            setSelectedPrompt(prompt);
                                            setQuery('');
                                            setSuggestionsVisible(false);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {prompt}
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedPrompt && (
                            <div className="selected-prompt">
                                <span
                                    className="remove-mark"
                                    onClick={() => {
                                        setSelectedPrompt('');
                                        setQuery('');
                                    }}
                                >
                                    ❌
                                </span>
                            </div>
                        )}

                        <p className='font_14 mb-0 color_white'>{(selectedPrompt || query).length}/270</p>
                        <button
                            className="send"
                            onClick={sendAiQuery}
                            disabled={isLoading || (!selectedPrompt && !query.trim())}
                        >
                            {isLoading ? <ButtonLoader /> : '→'}
                        </button>
                    </div>

                    <p className="credit mt-3 mb-0">
                        {remainingCredits > 0 ? (
                            `${remainingCredits}/100 Credit Limit`
                        ) : (
                            <>
                                {resMesg && `${resMesg} - `}
                                <span className='text-danger'>You have used all your credits. Please upgrade your plan.</span>
                            </>
                        )}
                    </p>
                </div>
            </div>

            {/* Row detail popup modal */}
            {modalRow && (
                <div className="row-detail-overlay" onClick={() => setModalRow(null)}>
                    <div className="row-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="row-detail-modal__header">
                            <span className="modal-title">Risk Details</span>
                            <button className="modal-close" onClick={() => setModalRow(null)}>✕</button>
                        </div>
                        <div className="row-detail-modal__body">
                            <div className="expanded-detail-grid">
                                {dynamicHeaders.map((key, idx) => (
                                    <div className={`detail-item ${isLongText(modalRow[key]) ? 'detail-item--full' : ''}`} key={idx}>
                                        <span className="detail-label" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            {headerLabel(key)}
                                            {key === 'Dwell Time' && (
                                                <TooltipText
                                                    src='https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/hover-text.svg'
                                                    alt="info"
                                                    tooltipText="Dwell Time is how long this risk has been present in your environment. A longer dwell time increases urgency — the multiplier shown reflects compounded exposure over time."
                                                    className='info_text'
                                                    height={12}
                                                    width={12}
                                                />
                                            )}
                                        </span>
                                        {(() => {
                                            const val = cellValue(modalRow[key]) || '—';
                                            // Split on numbered list pattern: " 1. " " 2. " etc.
                                            const parts = typeof val === 'string'
                                                ? val.split(/(?=\s*\d+\.\s)/).map(s => s.trim()).filter(Boolean)
                                                : null;
                                            if (parts && parts.length > 1) {
                                                return (
                                                    <ol className="detail-value detail-value--list">
                                                        {parts.map((p, i) => (
                                                            <li key={i}>{p.replace(/^\d+\.\s*/, '')}</li>
                                                        ))}
                                                    </ol>
                                                );
                                            }
                                            return <span className="detail-value">{val}</span>;
                                        })()}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NmtAI;
